import { PiiPolicy } from 'assemblyai';

// Minimum required PII policies - always enforced for security (must match backend)
export const MINIMUM_REQUIRED_PII_POLICIES: PiiPolicy[] = [
  'credit_card_number',
  'credit_card_cvv',
  'credit_card_expiration',
  'account_number',
  'banking_information',
];

export interface PiiPolicyCategory {
  name: string;
  description: string;
  policies: {
    value: PiiPolicy;
    label: string;
    description?: string;
  }[];
}

export const PII_POLICY_CATEGORIES: PiiPolicyCategory[] = [
  {
    name: 'Personal Identifiers',
    description: 'Names, IDs, and personal information',
    policies: [
      {
        value: 'person_name',
        label: 'Person Names',
        description: 'First and last names',
      },
      { value: 'person_age', label: 'Ages', description: 'Age references' },
      {
        value: 'date_of_birth',
        label: 'Date of Birth',
        description: 'Birth dates',
      },
      {
        value: 'gender_sexuality',
        label: 'Gender/Sexuality',
        description: 'Gender and sexuality references',
      },
      {
        value: 'nationality',
        label: 'Nationality',
        description: 'Country of origin',
      },
      {
        value: 'marital_status',
        label: 'Marital Status',
        description: 'Relationship status',
      },
      {
        value: 'religion',
        label: 'Religion',
        description: 'Religious affiliations',
      },
      {
        value: 'political_affiliation',
        label: 'Political Affiliation',
        description: 'Political party or views',
      },
      {
        value: 'zodiac_sign',
        label: 'Zodiac Sign',
        description: 'Astrological signs',
      },
    ],
  },
  {
    name: 'Contact Information',
    description: 'Email, phone, and addresses',
    policies: [
      {
        value: 'email_address',
        label: 'Email Addresses',
        description: 'Email contacts',
      },
      {
        value: 'phone_number',
        label: 'Phone Numbers',
        description: 'Telephone numbers',
      },
      {
        value: 'location',
        label: 'Locations',
        description: 'Physical addresses and places',
      },
      {
        value: 'ip_address',
        label: 'IP Addresses',
        description: 'Internet Protocol addresses',
      },
      { value: 'url', label: 'URLs', description: 'Web addresses' },
      {
        value: 'username',
        label: 'Usernames',
        description: 'Online usernames',
      },
      {
        value: 'password',
        label: 'Passwords',
        description: 'Account passwords',
      },
    ],
  },
  {
    name: 'Financial Information',
    description: 'Banking and credit card details',
    policies: [
      {
        value: 'credit_card_number',
        label: 'Credit Card Numbers',
        description: 'Card numbers',
      },
      {
        value: 'credit_card_cvv',
        label: 'CVV Codes',
        description: 'Card verification values',
      },
      {
        value: 'credit_card_expiration',
        label: 'Card Expiration',
        description: 'Expiry dates',
      },
      {
        value: 'account_number',
        label: 'Account Numbers',
        description: 'Bank account numbers',
      },
      {
        value: 'banking_information',
        label: 'Banking Info',
        description: 'Routing numbers, IBAN, etc.',
      },
      {
        value: 'money_amount',
        label: 'Money Amounts',
        description: 'Financial values',
      },
      {
        value: 'statistics',
        label: 'Statistics',
        description: 'Statistical data',
      },
    ],
  },
  {
    name: 'Medical Information',
    description: 'Health and medical data',
    policies: [
      {
        value: 'medical_condition',
        label: 'Medical Conditions',
        description: 'Diseases and conditions',
      },
      {
        value: 'medical_process',
        label: 'Medical Procedures',
        description: 'Treatments and procedures',
      },
      {
        value: 'healthcare_number',
        label: 'Healthcare Numbers',
        description: 'Insurance IDs',
      },
      {
        value: 'blood_type',
        label: 'Blood Type',
        description: 'Blood group information',
      },
      {
        value: 'drug',
        label: 'Medications',
        description: 'Drug names and prescriptions',
      },
      { value: 'injury', label: 'Injuries', description: 'Physical injuries' },
      {
        value: 'physical_attribute',
        label: 'Physical Attributes',
        description: 'Physical descriptions',
      },
    ],
  },
  {
    name: 'Government IDs',
    description: 'Official identification numbers',
    policies: [
      {
        value: 'us_social_security_number',
        label: 'SSN',
        description: 'US Social Security Numbers',
      },
      {
        value: 'drivers_license',
        label: "Driver's License",
        description: 'License numbers',
      },
      {
        value: 'passport_number',
        label: 'Passport Number',
        description: 'Passport IDs',
      },
      {
        value: 'vehicle_id',
        label: 'Vehicle ID',
        description: 'VIN, license plates',
      },
    ],
  },
  {
    name: 'Professional & Contextual',
    description: 'Work and general context',
    policies: [
      {
        value: 'occupation',
        label: 'Occupation',
        description: 'Job titles and roles',
      },
      {
        value: 'organization',
        label: 'Organization',
        description: 'Company names',
      },
      {
        value: 'event',
        label: 'Events',
        description: 'Event names and references',
      },
      {
        value: 'language',
        label: 'Languages',
        description: 'Spoken languages',
      },
      {
        value: 'filename',
        label: 'Filenames',
        description: 'File names and paths',
      },
    ],
  },
  {
    name: 'Time & Dates',
    description: 'Temporal information',
    policies: [
      { value: 'date', label: 'Dates', description: 'Calendar dates' },
      { value: 'time', label: 'Times', description: 'Clock times' },
      {
        value: 'date_interval',
        label: 'Date Intervals',
        description: 'Date ranges',
      },
      { value: 'duration', label: 'Durations', description: 'Time periods' },
    ],
  },
  {
    name: 'Other',
    description: 'Additional sensitive data',
    policies: [
      {
        value: 'number_sequence',
        label: 'Number Sequences',
        description: 'Serial numbers, codes',
      },
    ],
  },
];

// Get all PII policies as a flat array
export const ALL_PII_POLICIES: PiiPolicy[] = PII_POLICY_CATEGORIES.flatMap(
  (category) => category.policies.map((policy) => policy.value)
);

export const ALL_SELECTABLE_POLICIES: PiiPolicy[] = ALL_PII_POLICIES.filter(
  (policy) => !MINIMUM_REQUIRED_PII_POLICIES.includes(policy)
);

// Preset configurations
export interface PiiPreset {
  name: string;
  description: string;
  policies: PiiPolicy[];
}

export const PII_PRESETS: PiiPreset[] = [
  {
    name: 'Basic Protection',
    description:
      'Redact critical info like names, SSN, phone, and email (card/banking info always redacted)',
    policies: [
      'person_name',
      'phone_number',
      'email_address',
      'us_social_security_number',
    ],
  },
  {
    name: 'Healthcare',
    description:
      'HIPAA-compliant redaction for medical/healthcare calls (card/banking info always redacted)',
    policies: [
      'person_name',
      'date_of_birth',
      'phone_number',
      'email_address',
      'location',
      'medical_condition',
      'medical_process',
      'healthcare_number',
      'blood_type',
      'drug',
      'injury',
      'physical_attribute',
      'us_social_security_number',
    ],
  },
  {
    name: 'Financial',
    description:
      'Redact financial information (card/banking info always redacted)',
    policies: [
      'money_amount',
      'us_social_security_number',
      'person_name',
      'phone_number',
      'email_address',
    ],
  },
  {
    name: 'Everything',
    description:
      'Maximum protection - redact all detectable PII (card/banking info always redacted)',
    policies: ALL_SELECTABLE_POLICIES,
  },
];
